<template>
    <li :class="$options.name">
        <div class="block px-4 pt-4 pb-2 md:pb-1 EventEntryContent">
            <div class="flex flex-col md:flex-row md:justify-between">
                <p
                    class="mb-2 text-sm font-medium leading-5 text-primary EventEntryLeadingContent"
                >
                    {{ componentData.title }}
                </p>
                <div
                    class="flex md:flex-shrink-0 mb-2 md:ml-2 space-x-2 EventEntryCategories"
                >
                    <p
                        v-if="computedEventCategory"
                        class="inline-flex py-0.5 px-2.5 text-xs font-medium leading-4 text-gray-900 bg-gray-300 rounded-full cursor-pointer EventEntryBadge"
                        @click="setEventCategoryFilter(computedEventCategory)"
                    >
                        {{ computedEventCategory.label }}
                    </p>
                    <p
                        v-if="computedLocationCategory"
                        class="inline-flex py-0.5 px-2.5 text-xs font-medium leading-4 text-gray-900 bg-gray-300 rounded-full cursor-pointer EventEntryBadge"
                        @click="setLocationCategoryFilter(computedLocationCategory)"
                    >
                        {{ computedLocationCategory.label }}
                    </p>
                </div>
            </div>
            <div class="flex flex-col md:flex-row justify-between">
                <div class="flex flex-col md:pr-12 md:w-5/12 EventEntrySupportingContent">
                    <p
                        class="flex items-start mb-2 md:mb-3 text-sm font-normal leading-5 text-gray-500 EventEntryDate"
                    >
                        <CalendarIcon
                            class="flex-shrink-0 mr-2 w-5 h-5 text-gray-500"
                            aria-hidden="true"
                        />
                        {{ computedEventDate.date }}
                    </p>

                    <p
                        v-if="computedEventDate.time"
                        class="flex items-start mb-2 md:mb-3 text-sm font-normal leading-5 text-gray-500 EventEntryTime"
                    >
                        <ClockIcon
                            class="flex-shrink-0 mr-2 w-5 h-5 text-gray-500"
                            aria-hidden="true"
                        />
                        {{ computedEventDate.time }}
                    </p>

                    <div v-if="isInfoVisible">
                        <p
                            v-if="componentData.address"
                            class="flex items-start mb-2 md:mb-3 text-sm font-normal leading-5 text-gray-500 EventEntryLocation"
                        >
                            <LocationMarkerIcon
                                class="flex-shrink-0 mr-2 w-5 h-5 text-gray-500"
                                aria-hidden="true"
                            />
                            {{ componentData.address }}
                        </p>
                        <p
                            class="flex md:hidden items-start mb-2 md:mb-3 text-sm font-normal leading-5 text-gray-500 EventEntryDescriptionMobile"
                        >
                            <InformationCircleIcon
                                class="flex-shrink-0 mr-2 w-5 h-5 text-gray-500"
                                aria-hidden="true"
                            />
                            {{ componentData.description }}
                        </p>
                        <p
                            class="flex items-start mb-2 md:mb-3 text-sm font-normal leading-5 text-gray-500 EventEntryICAL"
                        >
                            <CalendarIcon
                                class="flex-shrink-0 mr-2 w-5 h-5 text-gray-500"
                                aria-hidden="true"
                            />
                            <a
                                :href="componentData.calendarUrl"
                                target="_blank"
                                class="mr-2 hover:underline"
                            >
                                Kalender
                            </a>
                        </p>
                    </div>
                </div>
                <div class="hidden md:flex flex-col justify-start w-7/12 max-w-[500px]">
                    <p
                        v-if="!isInfoVisible && componentData.address"
                        class="flex items-start mt-2 first:mt-0 ml-auto text-sm font-normal leading-5 text-gray-500 EventEntryLocationDesktop"
                    >
                        <LocationMarkerIcon
                            class="flex-shrink-0 mr-2 w-5 h-5 text-gray-500"
                            aria-hidden="true"
                        />
                        {{ componentData.address }}
                    </p>
                    <p
                        v-if="isInfoVisible && componentData.description"
                        class="flex items-start mt-2 first:mt-0 text-sm font-normal leading-5 text-gray-500 EventEntryDescriptionDesktop"
                    >
                        <InformationCircleIcon
                            class="flex-shrink-0 mr-2 w-5 h-5 text-gray-500"
                            aria-hidden="true"
                        />
                        {{ componentData.description }}
                    </p>

                    <p
                        v-if="isInfoVisible && componentData.url"
                        class="flex items-start mt-2 first:mt-0 text-sm font-normal leading-5 text-gray-500 EventEntryDescriptionDesktop"
                    >
                        <LinkIcon
                            class="flex-shrink-0 mr-2 w-5 h-5 text-gray-500"
                            aria-hidden="true"
                        />
                        <a
                            :href="componentData.url"
                            class="hover:text-gray-700 hover:underline"
                            target="_blank"
                        >
                            {{ componentData.url }}
                        </a>
                    </p>
                </div>
            </div>
        </div>
        <div class="flex justify-center items-center mb-2.5">
            <button
                class="focus:outline-none EventEntryStackedListItemButton"
                @click="toggleInfoVisibility"
            >
                <span
                    v-if="isInfoVisible"
                    class="flex items-center text-base font-medium leading-6 text-gray-500 hover:text-gray-700 cursor-pointer"
                >
                    <ChevronUpIcon
                        class="flex-shrink-0 mr-1.5 w-5 text-gray-400"
                        aria-hidden="true"
                    />
                    weniger
                </span>
                <span
                    v-if="!isInfoVisible"
                    class="flex items-center text-base font-medium leading-6 text-gray-500 hover:text-gray-700 cursor-pointer"
                >
                    <ChevronDownIcon
                        class="flex-shrink-0 mr-1.5 w-5 text-gray-400"
                        aria-hidden="true"
                    />
                    mehr
                </span>
            </button>
        </div>
    </li>
</template>

<script>
import { defineComponent } from 'vue'
import { mapActions, mapGetters } from 'vuex'
import {
    CalendarIcon,
    ClockIcon,
    InformationCircleIcon,
    LinkIcon,
    LocationMarkerIcon,
} from '@heroicons/vue/outline'
import { ChevronDownIcon, ChevronUpIcon } from '@heroicons/vue/solid'
import { format, fromUnixTime } from 'date-fns'
import { de } from 'date-fns/locale'
import { defaultCategoryFilterItem } from '@/services/store/modules/eventsStore.js'

export default defineComponent({
    name: 'EventEntry',
    components: {
        ClockIcon,
        LocationMarkerIcon,
        CalendarIcon,
        InformationCircleIcon,
        ChevronUpIcon,
        ChevronDownIcon,
        LinkIcon,
    },
    props: {
        componentData: {
            type: Object,
            default: () => ({
                id: null,
                title: null,
                description: null,
                organizer: null,
                start: null,
                end: null,
                allDay: null,
                address: null,
                url: null,
                categories: {
                    location: null,
                    type: null,
                },
                calendarUrl: null,
            }),
        },
    },
    data: function () {
        return {
            isInfoVisible: false,
        }
    },
    computed: {
        ...mapGetters('events', [
            'getEventCategory',
            'getCurrentEventCategory',
            'getLocationCategory',
            'getCurrentLocationCategory',
        ]),
        computedEventCategory() {
            return this.getEventCategory(this.componentData.categories.event)
        },
        computedLocationCategory() {
            return this.getLocationCategory(this.componentData.categories.location)
        },
        computedEventDate() {
            const startTime = fromUnixTime(this.componentData.start)
            const endTime = fromUnixTime(this.componentData.end)

            if (this.componentData.allDay === true) {
                return {
                    date: format(startTime, 'dd.MM.yyyy') + ' ' + '(ganztags)',
                    time: null,
                }
            }

            return {
                date: format(startTime, 'dd.MM.yyyy'),
                time: `(${format(startTime, 'EEEE', { locale: de })}) ${format(
                    startTime,
                    'H:mm'
                )} - ${format(endTime, 'H:mm')}`,
            }
        },
    },
    methods: {
        ...mapActions('events', {
            updateEventFilter: 'updateEventFilter',
            updateLocationFilter: 'updateLocationFilter',
        }),
        toggleInfoVisibility() {
            this.isInfoVisible = !this.isInfoVisible
        },
        setEventCategoryFilter(eventCategory) {
            if (eventCategory.id === this.getCurrentEventCategory.id) {
                this.$store.dispatch(
                    'events/updateEventFilter',
                    defaultCategoryFilterItem
                )
            } else {
                this.$store.dispatch('events/updateEventFilter', eventCategory)
            }
        },
        setLocationCategoryFilter(locationCategory) {
            if (locationCategory.id === this.getCurrentLocationCategory.id) {
                this.$store.dispatch(
                    'events/updateLocationFilter',
                    defaultCategoryFilterItem
                )
            } else {
                this.$store.dispatch('events/updateLocationFilter', locationCategory)
            }
        },
    },
})
</script>

<style scoped></style>
